import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, App, Button, Card, Empty, Modal, Space, Table, Tag, Typography } from "antd";
import { useState } from "react";
import { useNavigate } from "react-router";
import { api, unwrap } from "@/api/client";
import { useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { ScanInput } from "@/components/ScanInput";
import { useI18n } from "@/i18n";
import { NoWms, useWmsWarehouse } from "./common";

/**
 * Gudang > Barang batal (Plan §6.9): goods of cancelled orders still in the
 * warehouse. A parcel packed for a cancelled order is checked at the
 * outbound area: still here, its units go back to the shelves; already with
 * the courier, a return waits for it (aftersales, through the return dock).
 * Units picked for cancelled orders wait in totes, cart slots, PACKING or
 * KIRIM until a task takes them back.
 */
export function Cancelled() {
  const { t, formatDateTime, formatNumber } = useI18n();
  const { me } = useAuth();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { current, loading } = useWmsWarehouse();
  const [scanned, setScanned] = useState<{ id: string; orderNumber: string; trackingNo: string } | null>(null);
  const tz = me?.workspace?.timezone;
  const list = useQuery({
    queryKey: ["cancelled", current?.id],
    queryFn: () => unwrap(api.GET("/v1/warehouses/{warehouseID}/cancelled", { params: { path: { warehouseID: current!.id } } })),
    enabled: !!current,
  });
  const refresh = () => {
    for (const k of ["cancelled", "tasks", "locations", "aftersales"]) void queryClient.invalidateQueries({ queryKey: [k] });
  };
  const check = useMutation({
    mutationFn: ({ id, result }: { id: string; result: "in_house" | "with_courier" }) =>
      unwrap(api.POST("/v1/parcels/{parcelID}/check", { params: { path: { parcelID: id } }, body: { result } })),
    onSuccess: (res) => {
      refresh();
      setScanned(null);
      if (res.caseId) {
        void message.success(t("cx.toReturn"));
        navigate(`/app/aftersales/${res.caseId}`);
      } else if (res.taskId) {
        void message.success(t("cx.taskMade"));
        navigate(`/app/warehouse/tasks/${res.taskId}`);
      } else {
        void message.success(t("cx.checked"));
      }
    },
    onError: (err) => void message.error(errorText(err)),
  });
  const putBack = useMutation({
    mutationFn: (locationId: string) =>
      unwrap(api.POST("/v1/warehouses/{warehouseID}/cancelled/return", { params: { path: { warehouseID: current!.id } }, body: { locationId } })),
    onSuccess: (res) => {
      refresh();
      void message.success(t("cx.taskMade"));
      navigate(`/app/warehouse/tasks/${res.taskId}`);
    },
    onError: (err) => void message.error(errorText(err)),
  });
  const parcels = list.data?.parcels ?? [];
  const units = list.data?.units ?? [];
  // A scanned tracking or order number opens its parcel's check.
  const scan = (code: string) => {
    const c = code.trim().toUpperCase();
    const p = parcels.find((x) => x.trackingNo.toUpperCase() === c || x.orderNumber.toUpperCase() === c);
    if (!p) {
      void message.warning(t("cx.notFound", { code }));
      return false;
    }
    setScanned(p);
    return true;
  };

  if (!loading && !current) return <NoWms />;
  return (
    <>
      <Typography.Title level={2} style={{ margin: "0 0 16px", fontSize: 28 }}>
        {t("cx.title")}
      </Typography.Title>
      <Alert type="info" showIcon style={{ marginBottom: 16 }} title={t("cx.help")} />
      <Card size="small" title={t("cx.parcels", { n: formatNumber(parcels.length) })} style={{ marginBottom: 16 }}>
        <Space orientation="vertical" size={12} style={{ width: "100%" }}>
          <ScanInput onScan={scan} placeholder={t("cx.scanParcel")} />
          <Table
            rowKey="id"
            size="small"
            loading={list.isFetching && !list.data}
            dataSource={parcels}
            pagination={false}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("cx.noParcels")} /> }}
            columns={[
              { title: t("cx.order"), render: (_, p) => <span className="z-num">{p.orderNumber}</span> },
              { title: t("cx.tracking"), render: (_, p) => <span className="z-num">{p.trackingNo || "—"}</span> },
              { title: t("cx.shop"), render: (_, p) => `${p.shopName}${p.carrier ? ` · ${p.carrier}` : ""}` },
              { title: t("cx.units"), dataIndex: "units", width: 80, align: "right" },
              { title: t("cx.cancelledAt"), width: 170, render: (_, p) => (p.cancelledAt ? formatDateTime(p.cancelledAt, tz) : "—") },
              {
                title: "",
                width: 300,
                render: (_, p) => (
                  <Space>
                    <Button size="small" type="primary" loading={check.isPending} onClick={() => check.mutate({ id: p.id, result: "in_house" })}>
                      {t("cx.inHouse")}
                    </Button>
                    <Button size="small" danger loading={check.isPending} onClick={() => check.mutate({ id: p.id, result: "with_courier" })}>
                      {t("cx.withCourier")}
                    </Button>
                  </Space>
                ),
              },
            ]}
          />
        </Space>
      </Card>
      <Modal
        open={!!scanned}
        title={t("cx.checkTitle", { order: scanned?.orderNumber ?? "" })}
        onCancel={() => setScanned(null)}
        footer={
          scanned && (
            <Space>
              <Button danger loading={check.isPending} onClick={() => check.mutate({ id: scanned.id, result: "with_courier" })}>
                {t("cx.withCourier")}
              </Button>
              <Button type="primary" loading={check.isPending} onClick={() => check.mutate({ id: scanned.id, result: "in_house" })}>
                {t("cx.inHouse")}
              </Button>
            </Space>
          )
        }
      >
        <Typography.Paragraph>{t("cx.checkHelp")}</Typography.Paragraph>
        {scanned?.trackingNo && <Tag className="z-num">{scanned.trackingNo}</Tag>}
      </Modal>
      <Card size="small" title={t("cx.units")}>
        <Table
          rowKey={(u) => `${u.locationId}/${u.skuId}`}
          size="small"
          loading={list.isFetching && !list.data}
          dataSource={units}
          pagination={units.length > 50 ? { pageSize: 50, showSizeChanger: false } : false}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("cx.noUnits")} /> }}
          columns={[
            { title: t("cx.location"), render: (_, u) => <Tag className="z-num">{u.locationCode}</Tag> },
            {
              title: t("catalog.code"),
              render: (_, u) => (
                <Space orientation="vertical" size={0}>
                  <span className="z-num">{u.skuCode}</span>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {u.skuName}
                  </Typography.Text>
                </Space>
              ),
            },
            { title: t("cx.qty"), dataIndex: "quantity", width: 90, align: "right" },
            { title: t("cx.exOrders"), dataIndex: "orders", render: (v: string) => <span className="z-num">{v || "—"}</span> },
            {
              title: "",
              width: 170,
              render: (_, u) => (
                <Button size="small" loading={putBack.isPending && putBack.variables === u.locationId} onClick={() => putBack.mutate(u.locationId)}>
                  {t("cx.putBack")}
                </Button>
              ),
            },
          ]}
        />
      </Card>
    </>
  );
}
